import { useState } from "react"

/** ```choices フェンスブロックの解析結果。 */
export interface ChoicesSpec {
  question: string
  options: string[]
  multi: boolean
}

/** ```choices ブロックの本文を解析する（B2 選択肢カード・2026-07-05）。
 *
 *  形式（オーケストレーターのプロンプトと対）:
 *    1行目 = 質問文
 *    以降  = 「1. 選択肢」「① 選択肢」形式の行（最大8件まで採用）
 *    最終行が正確に `multi` なら複数選択（チェック式）
 *
 *  解析できない（質問が無い・選択肢が2件未満）場合は null を返し、呼び出し側は
 *  通常のコードブロックとして描画する＝壊れた形式でも読める（フォールバック）。 */
export function parseChoicesBlock(text: string): ChoicesSpec | null {
  const lines = (text || "").split("\n").map((l) => l.trim()).filter(Boolean)
  if (lines.length < 3) return null
  let multi = false
  if (lines[lines.length - 1] === "multi") {
    multi = true
    lines.pop()
  }
  const question = lines[0]
  const options: string[] = []
  for (const line of lines.slice(1)) {
    const m = /^(?:\d+[.)．]|[①②③④⑤⑥⑦⑧])\s*(.+)$/.exec(line)
    if (m && m[1].trim()) options.push(m[1].trim())
  }
  if (!question || options.length < 2 || options.length > 8) return null
  return { question, options, multi }
}

/** チャット内の選択肢カード。タップでその選択肢を**そのまま自分の発言として送信**する。
 *  カードは会話の補助輪——無視して自由入力してもよい（強制ではない）。下部の
 *  自由記述欄からは、提示された選択肢に無い答え・補足をカードから直接送れる。
 *
 *  活性なのは onSend が渡された時（＝会話の最後のメッセージにある間）だけ。
 *  回答して後続メッセージが付くと親が onSend を渡さなくなり、「回答済み」として
 *  薄く・操作不能で表示される。done のコンポーネント状態だけに頼ると、再描画や
 *  履歴リロードでリセットされ**同じカードから何度も送信できてしまう**
 *  （2026-07-05 実走で発覚・対策済み）。 */
export function ChoiceCard({
  spec,
  onSend,
}: {
  spec: ChoicesSpec
  onSend?: (text: string) => void
}) {
  const [done, setDone] = useState(false)
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [freeText, setFreeText] = useState("")

  // 回答済み（または非活性）: 親が onSend を渡していない＝もう答えられないカード。
  const answered = !onSend
  const inactive = done || answered
  const canSend = !!onSend && !done

  const sendSingle = (i: number) => {
    if (!canSend) return
    setPicked(new Set([i]))
    setDone(true)
    onSend!(spec.options[i])
  }

  const toggle = (i: number) => {
    if (inactive) return
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }

  const sendMulti = () => {
    if (!canSend || picked.size === 0) return
    setDone(true)
    onSend!(
      [...picked]
        .sort((a, b) => a - b)
        .map((i) => spec.options[i])
        .join("、")
    )
  }

  // 自由記述: 提示された選択肢に無い答え・補足をカードから直接送る（2026-07-06 実走FB:
  // 「AIの選択肢に加えて自由記述欄がほしい」）。multi でチェック済みがあれば
  // 「選択肢。自由記述」の形で1メッセージに結合する（選択＋補足の同時送信）。
  const sendFree = () => {
    const text = freeText.trim()
    if (!canSend || !text) return
    setDone(true)
    const sel = [...picked].sort((a, b) => a - b).map((i) => spec.options[i])
    onSend!(spec.multi && sel.length > 0 ? `${sel.join("、")}。${text}` : text)
  }

  return (
    <div
      className={`not-prose my-2 rounded-xl border border-gray-200 bg-white p-2.5 space-y-1.5 transition-opacity ${
        inactive ? "opacity-60" : ""
      }`}
    >
      <div className="text-xs text-gray-500 px-1">
        {answered
          ? "回答済みです"
          : spec.multi
            ? "使うものにチェック → 決定"
            : "タップで選択（そのまま送信されます）"}
      </div>
      {spec.options.map((opt, i) =>
        spec.multi ? (
          <label
            key={i}
            className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm ${
              picked.has(i)
                ? "border-emerald-500 bg-emerald-50"
                : "border-gray-200 bg-gray-50"
            } ${inactive ? "cursor-default" : "cursor-pointer hover:border-gray-400"}`}
          >
            <input
              type="checkbox"
              className="h-4 w-4 accent-emerald-600"
              checked={picked.has(i)}
              disabled={inactive}
              onChange={() => toggle(i)}
            />
            <span className="min-w-0 break-words">{opt}</span>
          </label>
        ) : (
          <button
            key={i}
            type="button"
            disabled={inactive}
            onClick={() => sendSingle(i)}
            className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm ${
              picked.has(i)
                ? "border-emerald-500 bg-emerald-50"
                : "border-gray-200 bg-gray-50"
            } ${inactive ? "cursor-default" : "cursor-pointer hover:border-gray-800 hover:bg-white"}`}
          >
            <span
              className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border text-[11px] ${
                picked.has(i)
                  ? "border-emerald-600 bg-emerald-600 text-white"
                  : "border-gray-400 text-gray-500"
              }`}
            >
              {i + 1}
            </span>
            <span className="min-w-0 break-words">{opt}</span>
          </button>
        )
      )}
      {spec.multi && !answered && (
        <button
          type="button"
          disabled={!canSend || picked.size === 0}
          onClick={sendMulti}
          className="rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:bg-gray-300"
        >
          {picked.size > 0 ? `この${picked.size}件で決定` : "選択してください"}
        </button>
      )}
      {!answered && (
        // 自由記述欄。送信は ⌘/Ctrl+Enter かボタンのみ（Enter 単独は送信しない＝
        // IME変換確定のEnterが誤送信になる事故を防ぐ・メイン入力欄と同じ規約）。
        <div className="flex items-center gap-1.5 pt-0.5">
          <input
            type="text"
            value={freeText}
            disabled={inactive}
            onChange={(e) => setFreeText(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                (e.metaKey || e.ctrlKey) &&
                !e.nativeEvent.isComposing
              )
                sendFree()
            }}
            placeholder="自分の言葉で伝える…（選択肢以外もOK）"
            className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-gray-400 focus:outline-none disabled:opacity-50"
          />
          <button
            type="button"
            disabled={!canSend || !freeText.trim()}
            onClick={sendFree}
            className="flex-none rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:bg-gray-300"
          >
            送信
          </button>
        </div>
      )}
    </div>
  )
}
